import { Injectable, inject } from '@angular/core';
import { catchError, map, tap, throwError } from 'rxjs';
import { LoyaltyCollectionService } from '@src/api';
import { AlertService } from '@src/core/services';

import { LoyaltyProgramCollection } from '../types';
import { collectionUIToCollectionAPi } from '../utils/collectionUIToCollectionApi';
import { collectionApiToCollectionUI } from '../utils/collectionApiToCollectionUI';
import { LoyaltyProgramPermService } from '../../services';

import {
  LpActiveCollectionsStoreService,
  LpAllCollectionsStoreService,
  LpCollectionsInfoStoreService,
  CollectionsStoreKeys,
} from './store';

@Injectable({
  providedIn: 'root',
})
export class LoyaltyProgramCollectionsService {
  private readonly loyaltyCollectionService = inject(LoyaltyCollectionService);
  private readonly alertService = inject(AlertService);
  private readonly lpActiveCollectionsStore = inject(LpActiveCollectionsStoreService);
  private readonly lpAllCollectionsStore = inject(LpAllCollectionsStoreService);
  private readonly lpCollectionsInfoStore = inject(LpCollectionsInfoStoreService);
  private readonly permService = inject(LoyaltyProgramPermService);

  loadActiveCollections() {
    if (!this.permService.allowActiveCollectionsViewing) {
      this.lpActiveCollectionsStore.setData(CollectionsStoreKeys.Active, []);
      return;
    }

    this.loyaltyCollectionService
      .getLoyaltyActiveCollections()
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
        tap(activeCollections => {
          this.lpActiveCollectionsStore.setData(CollectionsStoreKeys.Active, activeCollections);
        }),
      )
      .subscribe();
  }

  loadAllCollections() {
    this.loyaltyCollectionService
      .getLoyaltyCollections()
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
        tap(allCollections => {
          this.lpAllCollectionsStore.setData(CollectionsStoreKeys.All, allCollections);
        }),
      )
      .subscribe();
  }

  getCollectionById(id: string, unionId?: string, ref?: string) {
    return this.loyaltyCollectionService.getLoyaltyCollectionById(id, unionId, ref).pipe(
      map(item => {
        return collectionApiToCollectionUI(item);
      }),
      tap(collection => {
        this.lpCollectionsInfoStore.setData(collection.id, collection);
      }),
    );
  }

  createCollection(collectionData: LoyaltyProgramCollection) {
    const data = collectionUIToCollectionAPi(collectionData);

    return this.loyaltyCollectionService.addLoyaltyCollection(data).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  editCollection(collectionData: LoyaltyProgramCollection) {
    const data = collectionUIToCollectionAPi(collectionData);

    return this.loyaltyCollectionService.editLoyaltyCollection(data).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  deleteCollection(collectionId: string) {
    return this.loyaltyCollectionService.deleteLoyaltyCollection(collectionId).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  checkPush(collectionId: string) {
    return this.loyaltyCollectionService.manualSendPush(collectionId).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }
}
